import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@matillion/component-library'
import { useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'

import { GETSecretReferencesResponseParams } from 'api/types'
import { useDeleteSecretReference, useGetPermissions } from 'api/hooks'
import EllipsesActionItem, {
  ThemeType
} from 'components/EllipsesActionItem/EllipsesActionItem'
import ConfirmCancelModal from 'components/Modal/ProjectExplorerModal'
import { QueryKey } from 'constants/endpoint'
import { PermissionsType, SecretReferenceTypes } from 'types'

interface ActionCellProps {
  secret: GETSecretReferencesResponseParams
  onSecretDetailsHandler: VoidFunction
  onDeleteHandler: (secretName: string, isSuccess?: boolean) => void
}

const ActionCell: FunctionComponent<ActionCellProps> = ({
  secret: { id, name },
  onSecretDetailsHandler,
  onDeleteHandler
}) => {
  const { projectId } = useParams()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync, isLoading } = useDeleteSecretReference(projectId!)

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const { data: deletePermission } = useGetPermissions(
    projectId!,
    PermissionsType.DeleteSecrets
  )

  const onSwitchModal = () => setIsDeleteModalVisible((v) => !v)

  const onDelete = () => {
    mutateAsync({ values: { id } })
      .then(() => {
        queryClient.invalidateQueries([
          QueryKey.SECRET_REFERENCES,
          projectId!,
          SecretReferenceTypes.PASSWORD
        ])
        onSwitchModal()

        onDeleteHandler(name)
      })
      .catch(() => {
        onDeleteHandler(name, false)
      })
  }

  return (
    <>
      <EllipsesActionItem
        actions={[
          {
            title: t('secrets.secretDefinitionListing.actions.view'),
            action: onSecretDetailsHandler,
            icon: <Icon.Container />,
            dataTestId: 'ellipses-action-view-secret-details'
          },
          ...(deletePermission?.result
            ? [
                {
                  title: t('secrets.secretDefinitionListing.actions.delete'),
                  action: onSwitchModal,
                  icon: <Icon.Trash />,
                  type: 'danger' as ThemeType,
                  dataTestId: 'ellipses-action-delete-secret'
                }
              ]
            : [])
        ]}
      />

      {isDeleteModalVisible && (
        <ConfirmCancelModal
          onValidate={onDelete}
          onCancel={onSwitchModal}
          content={t('secrets.deleteSecretReference.modal.content')}
          validate={t('secrets.deleteSecretReference.modal.validate')}
          waiting={isLoading}
        />
      )}
    </>
  )
}

export default ActionCell
